import React, { useMemo } from 'react';
import { TSupplierFeature } from '@/api';
import { TreeCheckbox } from '@/components/common/TreeCheckbox';

interface SupplierFeaturesTreeProps {
  features: TSupplierFeature[];
  value: TSupplierFeature[];
  onChange?: (features: TSupplierFeature[]) => void;
  readOnly?: boolean;
  title?: string;
}

export const SupplierFeaturesTree: React.FC<SupplierFeaturesTreeProps> = ({
  features,
  value,
  onChange,
  readOnly = false,
  title = "Características del Proveedor"
}) => {
  // Build a node lookup map from base features by key for easy reference
  const nodeByKey = useMemo(() => {
    const map = new Map<string, TSupplierFeature>();
    const walk = (nodes: TSupplierFeature[]) => {
      nodes.forEach((n) => {
        map.set(n.key, n);
        if (n.children?.length) walk(n.children);
      });
    };
    walk(features);
    return map;
  }, [features]);

  // Collect enabled keys from an incoming value (tree or flat), robust to duplicates
  const collectEnabledKeys = (nodes: TSupplierFeature[] | undefined): Set<string> => {
    const enabled = new Set<string>();
    if (!nodes) return enabled;
    const visit = (arr: TSupplierFeature[]) => {
      arr.forEach((n) => {
        if (n.enabled) enabled.add(n.key);
        if (n.children?.length) visit(n.children);
      });
    };
    visit(nodes);
    return enabled;
  };

  // Given a set of selected keys, produce a deep clone of base features with only `enabled` changed
  const markEnabledOnTree = (base: TSupplierFeature[], selected: Set<string>): TSupplierFeature[] => {
    return base.map((n) => ({
      key: n.key,
      name: n.name,
      enabled: selected.has(n.key),
      children: n.children ? markEnabledOnTree(n.children, selected) : undefined,
    }));
  };

  // Prepare selected nodes for TreeCheckbox from the current value (enabled keys)
  const selectedNodes = useMemo(() => {
    const enabledKeys = collectEnabledKeys(value);
    // If no value provided or no enabled flags found, treat as ALL enabled by default
    if (!value || enabledKeys.size === 0) {
      // All keys from base
      features.forEach(function walk(n) {
        enabledKeys.add(n.key);
        n.children?.forEach(walk);
      });
    }
    return Array.from(enabledKeys)
      .map((k) => nodeByKey.get(k))
      .filter((n): n is TSupplierFeature => Boolean(n));
  }, [features, nodeByKey, value]);

  const handleChange = (newSelectedNodes: TSupplierFeature[]) => {
    const selected = new Set<string>(newSelectedNodes.map((n) => n.key));
    const fullTree = markEnabledOnTree(features, selected);
    onChange?.(fullTree);
  };

  return (
    <TreeCheckbox<TSupplierFeature>
      nodes={features}
      value={selectedNodes}
      onChange={handleChange}
      readOnly={readOnly}
      title={title}
      emptyMessage="No hay características configuradas"
      helperText="Selecciona características individuales. Las características padre e hijas son independientes."
      getNodeKey={(node) => node.key}
      getNodeLabel={(node) => node.name}
      getNodeChildren={(node) => node.children}
    />
  );
};